import { fetcher } from "@/lib/fetcher";
import { ApiResponse } from "./model";
import type { SystemSortField, SortOrder } from "@/types/sort";
import { revalidateSystemsCache } from "./system-cache";

console.log(
  "process.env.NEXT_PUBLIC_APP_URL: ",
  process.env.NEXT_PUBLIC_APP_URL || "❌ NOT SET",
);

export type SystemStatus = "active" | "draft" | "needs_auth";
export type SystemLifecycleStatus = "active" | "inactive" | "archived";
// AuthType mirrors backend enum. Phase 1 adds oauth2_cimd (Client ID Metadata Document)
// and oauth2_client_credentials (machine-to-machine) for MCP servers.
export type AuthType =
  | "api_key"
  | "basic_auth"
  | "bearer_token"
  | "oauth2"
  | "oauth2_cimd"
  | "oauth2_client_credentials"
  | "none"
  | "not_configured";
export type AuthStatus =
  | "valid"
  | "not_configured"
  | "missing_credentials"
  | "invalid";
// System connectors are OpenAPI-backed only now. MCP is a first-class entity
// (see mcps-service.ts) and no longer rides through System.

export interface SystemRecord {
  system_id: string;
  name: string;
  description?: string;
  system_type?: string;
  spec_count?: number;
  api_spec_count?: number; // From stats endpoint
  created_time?: string;
  last_updated_time?: string;
  last_updated?: string; // From stats endpoint
  status?: string; // Original status from database
  system_status?: SystemStatus; // Calculated server-side
  auth_type?: AuthType;
  auth_status?: AuthStatus; // Granular auth status
  ai_summary?: string; // AI-generated summary of the system
  has_shared_auth?: boolean; // Auth inherited from template, not configured by workspace
  tags?: string[]; // UI tag/badge keys (e.g., "system_template", "official")
  is_template?: boolean; // Whether this system is a template (derived from tags)
  is_owner?: boolean; // True when the system belongs to the current workspace (own); absent/false for ref/published templates of others
  read_only?: boolean; // BE serve-time flag: true → published template the caller does not own (hide edit/add/delete); absent = false
  icon_url?: string; // Optional URL for system icon, can be derived from tags or system_type
}

export interface OauthInfo {
  system_id: string;
  callback_url: string;
}

export interface SystemPayload {
  name: string;
  description?: string;
  system_type?: string;
}

export interface SystemUpdatePayload {
  name?: string;
  description?: string;
  ai_summary?: string;
  status?: SystemLifecycleStatus;
}

export interface ListSystemsParams {
  page?: number;
  pageSize?: number;
  query?: string;
  sortField?: SystemSortField;
  sortOrder?: SortOrder;
  statusFilter?: SystemStatus | "all";
}

export async function fetchSystems(params: ListSystemsParams = {}): Promise<{
  systems: SystemRecord[];
  total: number;
  page: number;
  pageSize: number;
}> {
  const {
    page = 1,
    pageSize = 12,
    query,
    sortField,
    sortOrder,
    statusFilter,
  } = params;

  // Build query parameters for API
  const queryParams = new URLSearchParams();

  // Add status filter
  if (statusFilter && statusFilter !== "all") {
    queryParams.set("status_filter", statusFilter);
  }

  // Add sorting
  if (sortField) {
    queryParams.set("sort_field", sortField);
    queryParams.set("sort_order", sortOrder || "desc");
  }

  // Add pagination
  const offset = (page - 1) * pageSize;
  queryParams.set("limit", pageSize.toString());
  queryParams.set("offset", offset.toString());

  // Fetch systems with query parameters
  const url = `/api/ai-transform/v1/system${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  const response = await fetcher<ApiResponse<SystemRecord[]>>(url);

  let systems = response.data ?? [];
  const total = response.total ?? systems.length;

  // Apply client-side search filtering if query is provided
  if (query && query.trim()) {
    const searchTerm = query.trim().toLowerCase();
    systems = systems.filter(
      (system) =>
        system.name?.toLowerCase().includes(searchTerm) ||
        system.description?.toLowerCase().includes(searchTerm) ||
        system.system_id?.toLowerCase().includes(searchTerm),
    );
  }

  return {
    systems,
    total,
    page,
    pageSize,
  };
}

export async function createSystem(
  payload: SystemPayload,
): Promise<SystemRecord> {
  const response = await fetcher<ApiResponse<SystemRecord[]>>(
    "/api/ai-transform/v1/system",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    await revalidateSystemsCache(response.data[0].system_id);
    return response.data[0];
  }

  throw new Error(response.message || "Failed to create system");
}

export async function updateSystem(
  systemId: string,
  updates: SystemUpdatePayload,
): Promise<SystemRecord> {
  const response = await fetcher<ApiResponse<SystemRecord[]>>(
    `/api/ai-transform/v1/system?system_id=${encodeURIComponent(systemId)}`,
    {
      method: "PUT",
      body: JSON.stringify(updates),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    await revalidateSystemsCache(systemId);
    return response.data[0];
  }

  throw new Error(response.message || "Failed to update system");
}

// Lifecycle changes use the dedicated status-only API route, keeping general
// connector metadata edits separate from availability changes.
export async function switchSystemStatus(
  systemId: string,
  status: Extract<SystemLifecycleStatus, "active" | "inactive">,
): Promise<SystemRecord> {
  const response = await fetcher<ApiResponse<SystemRecord[]>>(
    `/api/ai-transform/v1/system/switch-status?system_id=${encodeURIComponent(systemId)}`,
    {
      method: "PUT",
      body: JSON.stringify({ status }),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }

  throw new Error(response.message || "Failed to update system status");
}

export async function deleteSystem(systemId: string): Promise<boolean> {
  const response = await fetcher<ApiResponse<unknown>>(
    `/api/ai-transform/v1/system?system_id=${encodeURIComponent(systemId)}`,
    {
      method: "DELETE",
    },
  );

  if (response.status === "OK") {
    void revalidateSystemsCache(systemId).catch(() => undefined);
    return true;
  }

  if (response.message) {
    throw new Error(response.message);
  }

  return false;
}

/**
 * Fetch system with additional information (auth config, action counts, etc.)
 */
export async function getOauth2Info(
  systemId: string,
): Promise<OauthInfo | null> {
  const response = await fetcher<ApiResponse<OauthInfo[]>>(
    `/api/ai-transform/v1/oauth2-info?system_id=${encodeURIComponent(systemId)}`,
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }

  return null;
}
